import { NumberInput } from '@/components/ui/number-input';
import {
  convertField,
  resolveTargetField,
  type TargetField,
  type TargetUnit,
} from '@/lib/target';

interface TargetInputProps {
  label: string;
  explanation: string;
  field: TargetField;
  onChange: (field: TargetField) => void;
  /** Current scope size, for the "= N shows" preview. Pass 0 when unknown. */
  scopeSize: number;
  /** Optional suggested absolute count; renders a "Suggest N" shortcut. */
  suggestionCount?: number;
  disabled?: boolean;
}

/**
 * A target field: numeric value + a %/# unit toggle, a live "= N shows"
 * resolution, an explanation, and an optional suggestion shortcut.
 */
export function TargetInput({
  label,
  explanation,
  field,
  onChange,
  scopeSize,
  suggestionCount,
  disabled = false,
}: TargetInputProps) {
  const resolved = resolveTargetField(scopeSize, field);
  const setMode = (m: TargetUnit) => onChange(convertField(field, m, scopeSize));
  const showResolution = scopeSize > 0;
  const showSuggest =
    suggestionCount != null && suggestionCount !== resolved && !disabled;

  const unitButton = (m: TargetUnit, text: string) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setMode(m)}
      className={
        field.mode === m
          ? 'bg-primary px-2.5 py-2 text-sm text-primary-foreground'
          : 'px-2.5 py-2 text-sm text-muted-foreground hover:bg-accent'
      }
    >
      {text}
    </button>
  );

  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <p className="text-xs text-muted-foreground">{explanation}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <NumberInput
          min={0}
          allowEmpty={false}
          className="w-24 rounded-md border border-input bg-background px-3 py-2"
          value={field.value}
          disabled={disabled}
          onChange={(n) => onChange({ ...field, value: n ?? 0 })}
        />
        <div className="inline-flex overflow-hidden rounded-md border border-input">
          {unitButton('pct', '%')}
          {unitButton('count', '#')}
        </div>
        {showResolution && (
          <span className="text-sm text-muted-foreground">
            = {resolved} show{resolved === 1 ? '' : 's'}
            {field.mode === 'pct' ? ` of ${scopeSize}` : ''}
          </span>
        )}
        {showSuggest && (
          <button
            type="button"
            className="ml-auto text-xs text-primary underline"
            onClick={() => onChange({ mode: 'count', value: suggestionCount! })}
          >
            Suggest {suggestionCount}
          </button>
        )}
      </div>
    </div>
  );
}
